/**
 * pairUnpaired — heuristic visual pairing of PF and DP transactions
 * for a single ISIN, used to highlight the rows most likely responsible
 * for a QTY_MISMATCH on the verify page.
 *
 * HONEST CAVEAT
 * =============
 * This does NOT identify the "cause" of a mismatch — in net-balance
 * mode, every transaction contributes to the net, so there is no
 * unique cause. What this finds is the smallest set of rows on each
 * side with no close counterpart on the other side, given a date and
 * signed-quantity tolerance. The operator sees these as "rows worth
 * looking at first," not as a forensic conclusion.
 *
 * The UI labels these as "unpaired (no close match)" — never as
 * "the cause" or "responsible for the gap" — so we don't promise
 * something the math can't deliver.
 *
 * Algorithm
 * =========
 * 1. Build a list of PF rows (signed qty +ve for buys, -ve for sells,
 *    0 for dividends and other cash events).
 * 2. Build a parallel DP list (credit -> +qty, debit -> -qty).
 * 3. Exclude rows whose signedQty is 0 (dividends etc. can't drive a
 *    QTY mismatch).
 * 4. Greedy pairing pass: for each PF row, find the DP row with the
 *    same signedQty and the closest date (within ±7 days). If found,
 *    mark both paired. Otherwise PF row stays unpaired.
 * 5. Any DP rows not paired in step 4 are also unpaired.
 *
 * The 7-day window is generous on purpose: T+2 settlement is the
 * norm in Indian equities; weekends and holidays push that up. We'd
 * rather pair too eagerly than miss an obvious match.
 */

export interface PairCandidate {
  pair_id: string;
  date: string;           // ISO YYYY-MM-DD (may be empty for sparse rows)
  signed_qty: number;     // +ve = increases holding, -ve = decreases
  /** Optional: source broker string from PF row (e.g. "AxisDirect") */
  source?: string;
  /** Optional: raw DP description, used for Inter Depository matching */
  dp_description?: string;
}

export interface PairingResult {
  /** Set of pair_ids that have a close counterpart on the other side. */
  paired: Set<string>;
  /** Set of pair_ids with NO close counterpart. These are highlighted. */
  unpaired: Set<string>;
  /** Sum of signed qtys of unpaired PF rows. */
  unpaired_pf_qty: number;
  /** Sum of signed qtys of unpaired DP rows. */
  unpaired_dp_qty: number;
}

const DATE_TOLERANCE_DAYS = 7;
const QTY_TOLERANCE = 0.001;

/**
 * Parse an ISO date to a millisecond timestamp. Returns NaN for
 * empty/invalid strings so date-distance calculations fall back to
 * "infinite distance," meaning a row without a date won't accidentally
 * pair with anything.
 */
function dateMs(d: string): number {
  if (!d) return NaN;
  const t = Date.parse(d);
  return Number.isNaN(t) ? NaN : t;
}

/**
 * Find the best DP candidate for a given PF row: same signed_qty
 * (within tolerance) and closest date. Returns its index in the dp
 * array, or -1 if no candidate qualifies.
 *
 * Closest-date is preferred so that in cases with two same-qty
 * candidates we pair the operator-intuitive one (the one that
 * settled closest in time).
 */
function bestMatchIndex(
  pf: PairCandidate,
  dp: PairCandidate[],
  taken: Set<number>,
): number {
  const pfMs = dateMs(pf.date);
  let bestIdx = -1;
  let bestDistance = Infinity;
  for (let i = 0; i < dp.length; i++) {
    if (taken.has(i)) continue;
    const cand = dp[i];
    if (Math.abs(cand.signed_qty - pf.signed_qty) > QTY_TOLERANCE) continue;
    const candMs = dateMs(cand.date);
    // If either side lacks a date, distance is treated as max-tolerance
    // (we don't outright reject, since same-qty alone is meaningful).
    let distanceDays: number;
    if (Number.isNaN(pfMs) || Number.isNaN(candMs)) {
      distanceDays = DATE_TOLERANCE_DAYS;
    } else {
      distanceDays = Math.abs(pfMs - candMs) / (24 * 60 * 60 * 1000);
    }
    if (distanceDays > DATE_TOLERANCE_DAYS) continue;
    if (distanceDays < bestDistance) {
      bestDistance = distanceDays;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function pairUnpaired(
  pfRows: PairCandidate[],
  dpRows: PairCandidate[],
): PairingResult {
  const paired = new Set<string>();
  const unpaired = new Set<string>();
  const takenDp = new Set<number>();

  // Greedy pass over PF rows, ignoring zero-qty rows (dividends etc.
  // can't drive a quantity mismatch by definition).
  for (const pf of pfRows) {
    if (Math.abs(pf.signed_qty) < QTY_TOLERANCE) continue;
    const matchIdx = bestMatchIndex(pf, dpRows, takenDp);
    if (matchIdx >= 0) {
      paired.add(pf.pair_id);
      paired.add(dpRows[matchIdx].pair_id);
      takenDp.add(matchIdx);
    } else {
      unpaired.add(pf.pair_id);
    }
  }

  // ── Tier-4: AxisDirect off-market Inter Depository split-delivery ──────
  // After the standard greedy pass, attempt to pair still-unpaired AxisDirect
  // BUY rows against Inter Depository credit rows (single or grouped) where:
  //   - DP date is strictly after PF date and within 10 calendar days
  //   - qty matches exactly (single) OR a subset of DP rows sums to PF qty
  // Settlement code may or may not be present — both are accepted.
  const TIER4_DAYS = 10;

  const unpairedPfAxisDirect = pfRows.filter(p =>
    !paired.has(p.pair_id) &&
    Math.abs(p.signed_qty) > QTY_TOLERANCE &&
    (p.source ?? '').trim().toLowerCase() === 'axisdirect' &&
    p.signed_qty > 0,
  );

  const unpairedDpInterDep = dpRows
    .map((dp, idx) => ({ dp, idx }))
    .filter(({ dp, idx }) =>
      !takenDp.has(idx) &&
      Math.abs(dp.signed_qty) > QTY_TOLERANCE &&
      /inter depository transfer/i.test(dp.dp_description ?? ''),
    );

  /** Subset-sum: find indices in `amounts` summing to `target` within 0.001 */
  function findSubset(amounts: number[], target: number): number[] | null {
    const n = Math.min(amounts.length, 20);
    for (let mask = 1; mask < (1 << n); mask++) {
      let sum = 0;
      for (let i = 0; i < n; i++) if (mask & (1 << i)) sum += amounts[i];
      if (Math.abs(sum - target) <= 0.001) {
        const out: number[] = [];
        for (let i = 0; i < n; i++) if (mask & (1 << i)) out.push(i);
        return out;
      }
    }
    return null;
  }

  const claimedDpIdxT4 = new Set<number>();

  for (const pfCand of unpairedPfAxisDirect) {
    const pfQty    = pfCand.signed_qty;
    const pfDateMs = dateMs(pfCand.date);
    if (Number.isNaN(pfDateMs)) continue;

    const eligible = unpairedDpInterDep.filter(({ dp, idx }) => {
      if (claimedDpIdxT4.has(idx)) return false;
      const dpMs = dateMs(dp.date);
      if (Number.isNaN(dpMs)) return false;
      const diffDays = (dpMs - pfDateMs) / (24 * 60 * 60 * 1000);
      return diffDays > 0 && diffDays <= TIER4_DAYS;
    });

    if (eligible.length === 0) continue;

    // Try exact single-row match.
    const exactMatch = eligible.find(({ dp }) => Math.abs(dp.signed_qty - pfQty) <= 0.001);
    if (exactMatch) {
      paired.add(pfCand.pair_id);
      paired.add(exactMatch.dp.pair_id);
      unpaired.delete(pfCand.pair_id);
      unpaired.delete(exactMatch.dp.pair_id);
      claimedDpIdxT4.add(exactMatch.idx);
      takenDp.add(exactMatch.idx);
      continue;
    }

    // Try grouped subset-sum match.
    const amounts = eligible.map(({ dp }) => dp.signed_qty);
    const subsetIndices = findSubset(amounts, pfQty);
    if (subsetIndices !== null) {
      paired.add(pfCand.pair_id);
      unpaired.delete(pfCand.pair_id);
      for (const si of subsetIndices) {
        const { dp, idx } = eligible[si];
        paired.add(dp.pair_id);
        unpaired.delete(dp.pair_id);
        claimedDpIdxT4.add(idx);
        takenDp.add(idx);
      }
    }
  }
  // ── end Tier-4 ──────────────────────────────────────────────────────────

  // ── Tier-5: AxisDirect/off-market SELL → eDIS/Block Mechanism debit grouping ──
  // Mirrors the backend Tier-5 rule. Match still-unpaired AxisDirect/off-market
  // SELL rows against DP debit entries (eDIS, Block Mechanism, T+1, NORMAL/<code>)
  // on the same day or within 10 days, exact qty OR grouped subset sum.
  const TIER5_DAYS = 10;

  function isTier5SellSource(src?: string): boolean {
    const s = (src ?? '').trim().toLowerCase();
    return s === 'axisdirect' || s === 'offmarket' || s === 'off-market' || s === 'off market';
  }

  function isTier5SellDebitDesc(desc?: string): boolean {
    if (!desc) return false;
    return (
      /(?:NORMAL|TRADE FOR TRADE)\/\d+/i.test(desc) ||
      /edis/i.test(desc) ||
      /block mechanism/i.test(desc) ||
      /t\+1/i.test(desc) ||
      /axis securities/i.test(desc)
    );
  }

  const unpairedPfSell = pfRows.filter(p =>
    unpaired.has(p.pair_id) &&
    Math.abs(p.signed_qty) > QTY_TOLERANCE &&
    isTier5SellSource(p.source) &&
    p.signed_qty < 0,  // SELL
  );

  const unpairedDpSellDebit = dpRows
    .map((dp, idx) => ({ dp, idx }))
    .filter(({ dp, idx }) =>
      !takenDp.has(idx) &&
      Math.abs(dp.signed_qty) > QTY_TOLERANCE &&
      dp.signed_qty < 0 &&  // debit (negative contribution)
      isTier5SellDebitDesc(dp.dp_description),
    );

  const claimedDpIdxT5 = new Set<number>();

  for (const pfCand of unpairedPfSell) {
    const pfQty    = Math.abs(pfCand.signed_qty);
    const pfDateMs = dateMs(pfCand.date);
    if (Number.isNaN(pfDateMs)) continue;

    const eligible = unpairedDpSellDebit.filter(({ dp, idx }) => {
      if (claimedDpIdxT5.has(idx)) return false;
      const dpMs = dateMs(dp.date);
      if (Number.isNaN(dpMs)) return false;
      const diffDays = (dpMs - pfDateMs) / (24 * 60 * 60 * 1000);
      return diffDays >= 0 && diffDays <= TIER5_DAYS;  // same-day OK for SELL
    });

    if (eligible.length === 0) continue;

    // Exact single-row match.
    const exactMatch = eligible.find(({ dp }) => Math.abs(Math.abs(dp.signed_qty) - pfQty) <= 0.001);
    if (exactMatch) {
      paired.add(pfCand.pair_id);
      paired.add(exactMatch.dp.pair_id);
      unpaired.delete(pfCand.pair_id);
      unpaired.delete(exactMatch.dp.pair_id);
      claimedDpIdxT5.add(exactMatch.idx);
      takenDp.add(exactMatch.idx);
      continue;
    }

    // Grouped subset-sum.
    const amounts = eligible.map(({ dp }) => Math.abs(dp.signed_qty));
    const subsetIndices = findSubset(amounts, pfQty);
    if (subsetIndices !== null) {
      paired.add(pfCand.pair_id);
      unpaired.delete(pfCand.pair_id);
      for (const si of subsetIndices) {
        const { dp, idx } = eligible[si];
        paired.add(dp.pair_id);
        unpaired.delete(dp.pair_id);
        claimedDpIdxT5.add(idx);
        takenDp.add(idx);
      }
    }
  }
  // ── end Tier-5 ──────────────────────────────────────────────────────────

  // Any DP row not paired (and not zero-qty) is also unpaired.
  for (let i = 0; i < dpRows.length; i++) {
    if (takenDp.has(i)) continue;
    const dp = dpRows[i];
    if (Math.abs(dp.signed_qty) < QTY_TOLERANCE) continue;
    unpaired.add(dp.pair_id);
  }

  // Sum unpaired quantities per side for the operator summary.
  let unpaired_pf_qty = 0;
  let unpaired_dp_qty = 0;
  for (const pf of pfRows) {
    if (unpaired.has(pf.pair_id)) unpaired_pf_qty += pf.signed_qty;
  }
  for (const dp of dpRows) {
    if (unpaired.has(dp.pair_id)) unpaired_dp_qty += dp.signed_qty;
  }

  return { paired, unpaired, unpaired_pf_qty, unpaired_dp_qty };
}
